import React, { useState, useEffect } from 'react';
import {
  Building2,
  MapPin,
  TrendingUp,
  Euro,
  Users,
  Phone,
  Mail,
  ChevronRight,
  Search,
  Filter,
  Plus,
  BarChart3,
  Globe,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';

// Componente de tarjeta de franquicia
const FranchiseCard = ({ franchise, onClick }) => {
  const statusColors = {
    activa: 'bg-green-50 text-green-700 border-green-200',
    pendiente: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    inactiva: 'bg-gray-50 text-gray-600 border-gray-200'
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-100 p-5 cursor-pointer hover:border-[#3B82F6] transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#3B82F6] rounded-xl flex items-center justify-center text-white font-semibold text-lg">
            {franchise.codigo}
          </div>
          <div>
            <h4 className="font-medium text-[#111827]">{franchise.nombre}</h4>
            <p className="text-sm text-[#6B7280] flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {franchise.ciudad}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${statusColors[franchise.estado]}`}>
          {franchise.estado}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-lg font-semibold text-[#111827]">{franchise.facturacion}</p>
          <p className="text-xs text-[#6B7280]">Facturacion</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-[#111827]">{franchise.clientes}</p>
          <p className="text-xs text-[#6B7280]">Clientes</p>
        </div>
        <div>
          <p className="text-lg font-semibold text-[#111827]">{franchise.empleados}</p>
          <p className="text-xs text-[#6B7280]">Empleados</p>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-[#6B7280]">
        <span>Desde {franchise.fechaAlta}</span>
        <span className={franchise.tendencia.startsWith('+') ? 'text-green-600' : 'text-red-600'}>
          {franchise.tendencia} este mes
        </span>
      </div>
    </div>
  );
};

// Componente de resumen regional
const RegionSummary = ({ region }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 bg-blue-50 rounded-lg">
        <Globe className="w-4 h-4 text-blue-600" />
      </div>
      <div>
        <h4 className="font-medium text-[#111827]">{region.nombre}</h4>
        <p className="text-xs text-[#6B7280]">{region.franquicias} franquicias</p>
      </div>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-[#6B7280]">Facturacion</span>
        <span className="font-medium text-[#111827]">{region.facturacion}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-[#6B7280]">Clientes</span>
        <span className="font-medium text-[#111827]">{region.clientes}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#3B82F6] rounded-full"
          style={{ width: `${region.objetivo}%` }}
        />
      </div>
      <p className="text-xs text-[#6B7280] text-right">{region.objetivo}% objetivo</p>
    </div>
  </div>
);

export default function FranquiciasPanel() {
  const [activeTab, setActiveTab] = useState('franquicias');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [kpis] = useState({
    totalFranquicias: { value: 24, activas: 22 },
    facturacionTotal: { value: '€126.720', variacion: '+12%' },
    clientesTotales: { value: '1.245', variacion: '+8%' },
    empleadosTotales: { value: 89, variacion: '+5' }
  });

  const [franchises] = useState([
    { codigo: 'BCN', nombre: 'Manantial Barcelona Centro', ciudad: 'Barcelona', estado: 'activa', facturacion: '€8.450', clientes: 67, empleados: 4, fechaAlta: 'Mar 2024', tendencia: '+15%' },
    { codigo: 'MAD', nombre: 'Manantial Madrid Norte', ciudad: 'Madrid', estado: 'activa', facturacion: '€12.200', clientes: 89, empleados: 6, fechaAlta: 'Ene 2024', tendencia: '+22%' },
    { codigo: 'VAL', nombre: 'Manantial Valencia', ciudad: 'Valencia', estado: 'activa', facturacion: '€6.800', clientes: 45, empleados: 3, fechaAlta: 'Jun 2024', tendencia: '+8%' },
    { codigo: 'SEV', nombre: 'Manantial Sevilla', ciudad: 'Sevilla', estado: 'activa', facturacion: '€5.200', clientes: 38, empleados: 3, fechaAlta: 'Sep 2024', tendencia: '+5%' },
    { codigo: 'BIL', nombre: 'Manantial Bilbao', ciudad: 'Bilbao', estado: 'pendiente', facturacion: '€0', clientes: 0, empleados: 2, fechaAlta: 'Ene 2026', tendencia: '-' },
    { codigo: 'MLG', nombre: 'Manantial Malaga', ciudad: 'Malaga', estado: 'activa', facturacion: '€4.100', clientes: 32, empleados: 2, fechaAlta: 'Nov 2024', tendencia: '+18%' }
  ]);

  const [regions] = useState([
    { nombre: 'Cataluna', franquicias: 5, facturacion: '€24.500', clientes: 198, objetivo: 92 },
    { nombre: 'Madrid', franquicias: 4, facturacion: '€32.100', clientes: 245, objetivo: 88 },
    { nombre: 'Andalucia', franquicias: 6, facturacion: '€18.600', clientes: 142, objetivo: 75 },
    { nombre: 'Pais Vasco', franquicias: 3, facturacion: '€12.400', clientes: 98, objetivo: 65 }
  ]);

  const [pendingTasks] = useState([
    { franquicia: 'BCN', tarea: 'Renovacion contrato anual', fecha: '20 Ene', tipo: 'contrato' },
    { franquicia: 'MAD', tarea: 'Auditoria trimestral', fecha: '25 Ene', tipo: 'auditoria' },
    { franquicia: 'BIL', tarea: 'Inauguracion oficial', fecha: '15 Feb', tipo: 'apertura' },
    { franquicia: 'VAL', tarea: 'Formacion nuevo personal', fecha: '22 Ene', tipo: 'formacion' }
  ]);

  const tabs = [
    { id: 'franquicias', label: 'Franquicias', icon: Building2 },
    { id: 'regiones', label: 'Por region', icon: Globe },
    { id: 'rendimiento', label: 'Rendimiento', icon: BarChart3 }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Franquicias</h1>
          <p className="text-[#6B7280] mt-1">Gestion de la red de franquicias</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Nueva franquicia</span>
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Total franquicias</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.totalFranquicias.value}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.totalFranquicias.activas} activas</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Facturacion red</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.facturacionTotal.value}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <Euro className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.facturacionTotal.variacion} vs mes anterior</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Clientes totales</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.clientesTotales.value}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.clientesTotales.variacion} crecimiento</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Empleados red</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.empleadosTotales.value}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-xl">
              <Users className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.empleadosTotales.variacion} este mes</p>
        </div>
      </div>

      {/* Tabs y busqueda */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-[#111827] shadow-sm'
                  : 'text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder="Buscar franquicias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6] w-64"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            Filtros
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'franquicias' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {franchises.map((franchise, index) => (
              <FranchiseCard key={index} franchise={franchise} />
            ))}
          </div>

          {/* Sidebar - Tareas pendientes */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Proximas tareas</h3>
              <div className="space-y-3">
                {pendingTasks.map((task, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className={`p-1.5 rounded-lg ${
                      task.tipo === 'contrato' ? 'bg-blue-50' :
                      task.tipo === 'auditoria' ? 'bg-yellow-50' :
                      task.tipo === 'apertura' ? 'bg-green-50' : 'bg-purple-50'
                    }`}>
                      {task.tipo === 'contrato' ? <CheckCircle className="w-4 h-4 text-blue-600" /> :
                       task.tipo === 'auditoria' ? <AlertCircle className="w-4 h-4 text-yellow-600" /> :
                       task.tipo === 'apertura' ? <Building2 className="w-4 h-4 text-green-600" /> :
                       <Users className="w-4 h-4 text-purple-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#111827]">{task.tarea}</p>
                      <p className="text-xs text-[#6B7280]">{task.franquicia}</p>
                    </div>
                    <span className="text-xs text-[#6B7280]">{task.fecha}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Contacto rapido</h3>
              <div className="space-y-2">
                {franchises.slice(0, 3).map((franchise, index) => (
                  <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#3B82F6] rounded-lg flex items-center justify-center text-white text-xs font-medium">
                        {franchise.codigo}
                      </div>
                      <span className="text-sm text-[#111827]">{franchise.ciudad}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <Phone className="w-4 h-4 text-[#6B7280]" />
                      </button>
                      <button className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <Mail className="w-4 h-4 text-[#6B7280]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'regiones' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {regions.map((region, index) => (
            <RegionSummary key={index} region={region} />
          ))}
        </div>
      )}

      {activeTab === 'rendimiento' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-[#111827]">Ranking de franquicias</h2>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-medium text-[#6B7280]">#</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#6B7280]">Franquicia</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#6B7280]">Facturacion</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#6B7280]">Clientes</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#6B7280]">Crecimiento</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#6B7280]">Estado</th>
              </tr>
            </thead>
            <tbody>
              {franchises
                .filter(f => f.estado === 'activa')
                .sort((a, b) => parseFloat(b.facturacion.replace(/[€.]/g, '').replace(',', '.')) - parseFloat(a.facturacion.replace(/[€.]/g, '').replace(',', '.')))
                .map((franchise, index) => (
                  <tr key={index} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-[#111827]">{index + 1}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#3B82F6] rounded-lg flex items-center justify-center text-white text-xs font-medium">
                          {franchise.codigo}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#111827]">{franchise.nombre}</p>
                          <p className="text-xs text-[#6B7280]">{franchise.ciudad}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-[#111827]">{franchise.facturacion}</td>
                    <td className="px-6 py-4 text-sm text-[#6B7280]">{franchise.clientes}</td>
                    <td className="px-6 py-4">
                      <span className={`text-sm font-medium ${
                        franchise.tendencia.startsWith('+') ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {franchise.tendencia}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700">
                        activa
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
